import { usePortfolioTutorialsState } from "@/tools/aggregate/portfolio-export/PortfolioTutorialContext";
import { BellOutlined, CheckCircleFilled, PlusOutlined, WarningFilled } from "@ant-design/icons";
import { Button, Popover, Row, Switch, Tooltip } from "antd";
import { FC, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { JobSchema } from "../types/JobSchema";
import { NewRunModal } from "./NewRunModal";
export interface JobTableHeaderProps<T extends JobSchema> {
  onRunStart: (schema: T) => void;
  children?: ReactNode;
  newRunModalComponent?: FC;
  openModalByDefault?: boolean;
  requestNotification?: boolean;
  actionBtns?: JSX.Element;
  disableNewRun?: boolean;
  disableNewRunTooltip?: string;
}
export const JobTableHeader = <T extends JobSchema,>({
  children,
  newRunModalComponent,
  onRunStart,
  openModalByDefault,
  requestNotification = false,
  actionBtns,
  disableNewRun = false,
  disableNewRunTooltip
}: JobTableHeaderProps<T>) => {
  const [modalKey, setModalKey] = useState<string>();
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.remoteJobs"
  });
  const Modal = newRunModalComponent || NewRunModal;
  const {
    isTutorialNewRunModalOpen,
    newRunModalTrigger,
    alertBellTrigger,
    listFilters
  } = usePortfolioTutorialsState();
  const openModal = () => {
    setModalKey(v4());
  };
  useEffect(() => {
    if (openModalByDefault) {
      openModal();
    }
  }, []);
  return <Row justify="space-between" className="pb-4" data-sentry-element="Row" data-sentry-component="JobTableHeader" data-sentry-source-file="JobTableHeader.tsx">
      <div className="flex w-full items-center justify-between">
        <div ref={listFilters} className="flex w-full items-center justify-start gap-2">
          {actionBtns}
        </div>
        <div className="flex w-full items-center justify-end gap-2">
          <Tooltip title={disableNewRun ? disableNewRunTooltip : undefined} data-sentry-element="Tooltip" data-sentry-source-file="JobTableHeader.tsx">
            <Button ref={newRunModalTrigger} onClick={openModal} type="primary" icon={<PlusOutlined />} disabled={disableNewRun} data-sentry-element="Button" data-sentry-source-file="JobTableHeader.tsx">
              {t("startNewRun")}
            </Button>
          </Tooltip>

          {requestNotification && <div ref={alertBellTrigger}>
              <RequestNotification />
            </div>}
        </div>
      </div>

      {children}
      <Modal<T> footer={<></>} key={modalKey} onCancel={() => setModalKey("")} onRunStart={schema => {
      onRunStart(schema);
      setModalKey("");
    }} open={!!modalKey || isTutorialNewRunModalOpen} data-sentry-element="Modal" data-sentry-source-file="JobTableHeader.tsx" />
    </Row>;
};
const RequestNotification = () => {
  const [open, setOpen] = useState(false);
  const [notificationSupport, setNotificationSupport] = useState<"none" | "default" | "denied" | "granted">("none");
  const notifGranted = notificationSupport === "granted";
  const notifDenied = notificationSupport === "denied" || notificationSupport === "none";
  useEffect(() => {
    // If no notification support is detected at all, use "none".
    setNotificationSupport(typeof Notification === "undefined" ? "none" : Notification.permission);
  }, []);
  const requestNotifPerms = () => {
    if (notificationSupport === "default") {
      Notification.requestPermission(() => {
        setNotificationSupport(Notification.permission);
      });
    }
  };
  const getNotifBlurb = () => {
    switch (notificationSupport) {
      case "default":
        return "Turn on to receive notifications when your portfolio analysis is ready.";
      case "denied":
        return <>
            You’ve opted out of browser notifications.{" "}
            <a href="https://support.humblebundle.com/hc/en-us/articles/360008513933-Enabling-and-Disabling-Browser-Notifications-in-Various-Browsers" target="_blank" rel="noreferrer" style={{
            textDecoration: "underline"
          }}>
              Click here for support.
            </a>
          </>;
      case "granted":
        return <>
            Notifications are enabled. You will be notified when the portfolios finishes.{" "}
            <a href="https://support.humblebundle.com/hc/en-us/articles/360008513933-Enabling-and-Disabling-Browser-Notifications-in-Various-Browsers" target="_blank" rel="noreferrer" style={{
            textDecoration: "underline"
          }}>
              Click here to manage.
            </a>
          </>;
      case "none":
        return "Your browser does not support notifications.";
    }
  };
  const BellOutlinedWithSlash = () => <div className="relative" data-sentry-component="BellOutlinedWithSlash" data-sentry-source-file="JobTableHeader.tsx">
      <BellOutlined className="text-base" data-sentry-element="BellOutlined" data-sentry-source-file="JobTableHeader.tsx" />
      <span className="absolute left-[31%] top-[27%] h-full w-full origin-center rotate-45 transform border-l-[1.5px] border-gray-800 text-base"></span>
    </div>;
  const PopOverContent = () => <div className="w-full pb-1 pt-0.5 text-base" data-sentry-component="PopOverContent" data-sentry-source-file="JobTableHeader.tsx">
      <div className="flex justify-between">
        <div>Notifications</div>
        <div className="flex gap-1">
          {notifGranted ? <CheckCircleFilled className="text-green-600" /> : <>
              {notifDenied && <WarningFilled className="text-red-500" />}
              <Switch checked={false} onChange={() => requestNotifPerms()} disabled={notifDenied} />
            </>}
        </div>
      </div>
      <div className="pt-2 text-sm text-gray-500">{getNotifBlurb()}</div>
    </div>;
  return <>
      <Popover open={open} placement="bottomRight" onOpenChange={() => setOpen(!open)} trigger={"click"} rootClassName="!w-[300px]" content={<PopOverContent />} data-sentry-element="Popover" data-sentry-source-file="JobTableHeader.tsx">
        <Button className="flex items-center gap-1 text-gray-700" icon={notificationSupport === "denied" ? <BellOutlinedWithSlash /> : <BellOutlined />} data-sentry-element="Button" data-sentry-source-file="JobTableHeader.tsx" />
      </Popover>
    </>;
};